(function () {
  var initialized = new WeakSet();

  function text(value, fallback) {
    var raw = String(value || "").replace(/\s+/g, " ").trim();
    return raw || fallback || "";
  }

  function rating(value) {
    var parsed = Number(value);
    if (!Number.isFinite(parsed)) return 5;
    return Math.max(1, Math.min(5, Math.round(parsed)));
  }

  function initials(name) {
    var cleaned = text(name, "G");
    var parts = cleaned.split(" ").filter(Boolean);
    var first = parts[0] ? parts[0].charAt(0) : "G";
    var second = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
    return (first + second).toUpperCase().slice(0, 2);
  }

  function makeElement(tag, className, value) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (value != null) element.textContent = value;
    return element;
  }

  function quotePreview(value) {
    var full = text(value, "");
    if (full.length <= 62) return full;
    var clipped = full.slice(0, 62).replace(/\s+\S*$/, "").replace(/[.,;:!?]+$/, "");
    return clipped + "…";
  }

  function shouldShowReadMore(review) {
    var full = text(review.text, "");
    return quotePreview(full) !== full || full.length > 118;
  }

  // Tri par langue de page : avis dans la langue du visiteur d'abord,
  // puis les avis sans langue, puis les autres (ordre stable).
  function sortForLang(reviews, pageLang) {
    if (!pageLang) return reviews;
    function weight(review) {
      var lang = text(review.lang, "").toLowerCase();
      if (!lang) return 1;
      return lang === pageLang ? 0 : 2;
    }
    return reviews
      .map(function (review, index) { return { review: review, index: index, weight: weight(review) }; })
      .sort(function (a, b) { return a.weight - b.weight || a.index - b.index; })
      .map(function (entry) { return entry.review; });
  }

  function reviewListFromPayload(payload, pageLang) {
    var reviews = payload && Array.isArray(payload.reviews) ? payload.reviews : [];
    return sortForLang(reviews.filter(function (review) {
      return text(review.text, "") && rating(review.rating) >= 1;
    }), pageLang);
  }

  function audioListFromPayload(payload) {
    var audios = payload && Array.isArray(payload.audios) ? payload.audios : [];
    return audios.filter(function (audio) { return audio && text(audio.audioUrl, ""); });
  }

  function readFallback(root) {
    var source = root.querySelector("[data-bfl-google-reviews-fallback]");
    if (!source) return { reviews: [], audios: [] };
    try {
      var payload = JSON.parse(source.textContent || "{}");
      return {
        reviews: reviewListFromPayload(payload, text(root.dataset.lang, "")),
        audios: audioListFromPayload(payload)
      };
    } catch (error) {
      return { reviews: [], audios: [] };
    }
  }

  function photoUrl(value) {
    var photo = text(value, "");
    if (/^\/\//.test(photo)) return window.location.protocol + photo;
    if (/^https:\/\//i.test(photo)) return photo;
    return "";
  }

  /* ---------- Design configurable (admin /admin/google-reviews) ---------- */
  function hexToRgba(hex, alphaPct) {
    var m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || ""));
    if (!m) return String(hex || "");
    var alpha = Math.max(0, Math.min(100, Number(alphaPct))) / 100;
    return "rgba(" + parseInt(m[1], 16) + "," + parseInt(m[2], 16) + "," + parseInt(m[3], 16) + "," + alpha + ")";
  }

  var CONFIG_SHADOWS = {
    none: "none",
    soft: "0 10px 26px rgba(23,23,23,.08), inset 0 1px 0 rgba(255,255,255,.7)",
    deep: "0 18px 44px rgba(23,23,23,.14), inset 0 1px 0 rgba(255,255,255,.95), inset 0 -1px 0 rgba(255,255,255,.35)"
  };

  function applyConfig(root, cfg) {
    if (!cfg || typeof cfg !== "object") return;
    var s = root.style;
    function set(name, value) { s.setProperty(name, value); }
    if (Number.isFinite(Number(cfg.radius))) set("--grw-radius", Number(cfg.radius) + "px");
    if (Number.isFinite(Number(cfg.blur))) set("--grw-filter", Number(cfg.blur) > 0 ? "blur(" + Number(cfg.blur) + "px) saturate(1.35)" : "none");
    if (cfg.bgA && cfg.bgB) {
      var angle = Number.isFinite(Number(cfg.bgAngle)) ? Number(cfg.bgAngle) : 135;
      set("--grw-bg", "linear-gradient(" + angle + "deg, " + hexToRgba(cfg.bgA, cfg.bgAlphaA != null ? cfg.bgAlphaA : 100) + ", " + hexToRgba(cfg.bgB, cfg.bgAlphaB != null ? cfg.bgAlphaB : 100) + ")");
    }
    if (cfg.borderColor) set("--grw-border", hexToRgba(cfg.borderColor, cfg.borderAlpha != null ? cfg.borderAlpha : 100));
    if (cfg.shadow && CONFIG_SHADOWS[cfg.shadow]) set("--grw-shadow", CONFIG_SHADOWS[cfg.shadow]);
    if (cfg.sheen !== undefined) set("--grw-sheen-display", cfg.sheen ? "block" : "none");
    if (cfg.quoteColor) set("--grw-quote-color", String(cfg.quoteColor));
    if (Number.isFinite(Number(cfg.quoteSize))) set("--grw-quote-size", Number(cfg.quoteSize) + "px");
    if (cfg.quoteItalic !== undefined) set("--grw-quote-style", cfg.quoteItalic ? "italic" : "normal");
    if (cfg.authorColor) set("--grw-author-color", String(cfg.authorColor));
    if (Number.isFinite(Number(cfg.metaSize))) set("--grw-meta-size", Number(cfg.metaSize) + "px");
    if (cfg.starColor) set("--grw-star-color", String(cfg.starColor));
    if (cfg.starOffColor) set("--grw-star-off", String(cfg.starOffColor));
    if (Number.isFinite(Number(cfg.starSize))) set("--grw-star-size", Number(cfg.starSize) + "px");
    if (cfg.avatarShow !== undefined) {
      set("--grw-avatar-display", cfg.avatarShow ? "block" : "none");
      set("--grw-card-cols", cfg.avatarShow ? "46px minmax(0,1fr)" : "minmax(0,1fr)");
      set("--grw-card-cols-m", cfg.avatarShow ? "42px minmax(0,1fr)" : "minmax(0,1fr)");
    }
    if (cfg.avatarShape) set("--grw-avatar-radius", cfg.avatarShape === "rounded" ? "12px" : "999px");
    if (cfg.avatarColor) set("--grw-avatar-color", String(cfg.avatarColor));
    if (cfg.avatarBorder) set("--grw-avatar-border", String(cfg.avatarBorder));
    if (cfg.avatarBg) set("--grw-avatar-bg", String(cfg.avatarBg));
    if (cfg.googleShow !== undefined) set("--grw-google-display", cfg.googleShow ? "inline-grid" : "none");
    if (cfg.dotsColor) set("--grw-dot-color", String(cfg.dotsColor));
    if (cfg.dotsStyle) set("--grw-dot-active-w", cfg.dotsStyle === "dot" ? "6px" : "16px");
    if (cfg.footerAlign) set("--grw-footer-justify", cfg.footerAlign === "center" ? "center" : "space-between");
    if (cfg.seeAllShow !== undefined) set("--grw-seeall-display", cfg.seeAllShow ? "inline" : "none");
    if (cfg.seeAllColor) set("--grw-seeall-color", String(cfg.seeAllColor));
    if (cfg.audioAccent) set("--grw-audio-accent", String(cfg.audioAccent));
    root.classList.toggle("grw-audio-off", cfg.audioShow === false);
    if (Number.isFinite(Number(cfg.autoplayMs))) root.dataset.autoplayInterval = String(Number(cfg.autoplayMs));
    if (cfg.transition) root.setAttribute("data-grw-transition", String(cfg.transition));
  }

  /* ---------- SVG ---------- */
  var STAR_SVG =
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>';
  // Le G en monochrome : les quatre arcs se fondent en un seul pictogramme qui
  // hérite de la couleur de la carte (currentColor). Pas d'hexes en dur, donc il
  // suit le thème clair comme sombre sans qu'on ait à le repeindre.
  var GOOGLE_G_SVG =
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.1 3.57-5.17 3.57-8.81z"/><path d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.88-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.72-4.95H1.28v3.11A12 12 0 0 0 12 24z"/><path d="M5.28 14.28a7.2 7.2 0 0 1 0-4.56V6.61H1.28a12 12 0 0 0 0 10.78l4-3.11z"/><path d="M12 4.77c1.76 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.28 6.61l4 3.11C6.22 6.88 8.87 4.77 12 4.77z"/></svg>';
  var PLAY_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"/></svg>';
  var PAUSE_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6.5" y="5" width="4" height="14" rx="1"/><rect x="13.5" y="5" width="4" height="14" rx="1"/></svg>';

  function makeStars(review, label) {
    var count = rating(review.rating);
    var wrap = makeElement("span", "bfl-google-reviews__stars");
    for (var i = 1; i <= 5; i += 1) {
      var star = makeElement("i", "bfl-google-reviews__star" + (i <= count ? " is-on" : ""));
      star.innerHTML = STAR_SVG;
      wrap.appendChild(star);
    }
    wrap.setAttribute("role", "img");
    wrap.setAttribute("aria-label", label + " · " + count + "/5");
    return wrap;
  }

  function makeAvatar(review, fallbackPhotoUrl) {
    var avatar = makeElement("div", "bfl-google-reviews__avatar");
    var photo = photoUrl(review.authorPhotoUrl) || photoUrl(fallbackPhotoUrl);
    if (photo) {
      var image = document.createElement("img");
      image.src = photo;
      image.alt = "";
      image.loading = "lazy";
      image.decoding = "async";
      avatar.appendChild(image);
      return avatar;
    }
    avatar.appendChild(makeElement("span", "bfl-google-reviews__avatar-fallback", initials(review.authorName)));
    return avatar;
  }

  function openReviewDialog(root, review) {
    var dialog = makeElement("dialog", "bfl-google-reviews__dialog");
    var inner = makeElement("div", "bfl-google-reviews__dialog-inner");
    var head = makeElement("div", "bfl-google-reviews__dialog-head");
    var author = makeElement("div", "bfl-google-reviews__dialog-author", text(review.authorName, "Google"));
    var close = makeElement("button", "bfl-google-reviews__dialog-close", "×");
    var body = makeElement("p", "bfl-google-reviews__dialog-text", "“" + text(review.text, "") + "”");

    close.type = "button";
    close.setAttribute("aria-label", text(root.dataset.closeLabel, "Close"));
    close.addEventListener("click", function () { dialog.close(); });
    dialog.addEventListener("close", function () { dialog.remove(); });
    dialog.addEventListener("click", function (event) {
      if (event.target === dialog) dialog.close();
    });

    head.appendChild(author);
    head.appendChild(close);
    inner.appendChild(head);
    inner.appendChild(makeStars(review, text(root.dataset.ratingLabel, "Google review")));
    inner.appendChild(body);
    dialog.appendChild(inner);
    root.appendChild(dialog);
    if (dialog.showModal) dialog.showModal();
    else dialog.setAttribute("open", "");
    close.focus();
  }

  function makeSlide(root, review, index) {
    var slide = makeElement("article", "bfl-google-reviews__slide");
    if (index === 0) slide.classList.add("is-active");

    var card = makeElement("div", "bfl-google-reviews__card");
    var content = makeElement("div", "bfl-google-reviews__content");
    var quote = makeElement("blockquote", "bfl-google-reviews__quote", "“" + quotePreview(review.text) + "”");
    var meta = makeElement("div", "bfl-google-reviews__meta");
    var author = makeElement("span", "bfl-google-reviews__author", "— " + text(review.authorName, "Google"));
    var source = makeElement("span", "bfl-google-reviews__source");
    var googleMark = makeElement("span", "bfl-google-reviews__google-mark");
    googleMark.innerHTML = GOOGLE_G_SVG;

    source.appendChild(googleMark);
    meta.appendChild(author);
    meta.appendChild(makeStars(review, text(root.dataset.ratingLabel, "Google review")));
    meta.appendChild(source);
    if (shouldShowReadMore(review)) {
      var readMore = makeElement("button", "bfl-google-reviews__read-more", text(root.dataset.readMoreLabel, "Read more"));
      readMore.type = "button";
      readMore.addEventListener("click", function () { openReviewDialog(root, review); });
      meta.appendChild(readMore);
    }
    content.appendChild(quote);
    content.appendChild(meta);
    card.appendChild(makeAvatar(review, index === 0 ? root.dataset.firstReviewPhotoUrl : ""));
    card.appendChild(content);
    slide.appendChild(card);
    return slide;
  }

  /* ---------- Bloc « voix » (notes vocales clientes) ----------
     Bloc autonome, au-dessus de la carte Avis Google : les notes vocales ne
     viennent pas des clientes qui ont laissé un avis. La phrase saisie dans
     l'admin porte l'envie d'écouter ; le lecteur n'est que la preuve. */
  function formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
    var mins = Math.floor(seconds / 60);
    var secs = Math.floor(seconds % 60);
    return mins + ":" + (secs < 10 ? "0" : "") + secs;
  }

  // Mémoire « la boutique a-t-elle des notes vocales ? » : évite de réserver la
  // hauteur du bloc voix sur une boutique qui n'en a aucune.
  var AUDIO_MEMO_KEY = "bfl-grw-has-audio";

  function readAudioMemo() {
    try { return window.localStorage.getItem(AUDIO_MEMO_KEY); } catch (error) { return null; }
  }

  function writeAudioMemo(hasAudio) {
    try { window.localStorage.setItem(AUDIO_MEMO_KEY, hasAudio ? "1" : "0"); } catch (error) { /* noop */ }
  }

  // Nom de la langue écrit dans la langue de la page : "ar" devient « arabe »
  // sur une page française, "Arabic" sur une page anglaise. Intl.DisplayNames
  // évite d'entretenir 6 × 6 libellés à la main. En repli (vieux Safari) on
  // affiche l'autonyme — la cliente concernée le reconnaît de toute façon.
  var LANG_AUTONYMS = { fr: "Français", en: "English", ar: "العربية", es: "Español", nl: "Nederlands", he: "עברית" };
  var RTL_LANGS = { ar: 1, he: 1 };

  function languageName(code, pageLang) {
    var key = String(code || "").trim().toLowerCase();
    if (!key) return "";
    try {
      var label = new Intl.DisplayNames([pageLang || "fr"], { type: "language" }).of(key);
      if (label && label.toLowerCase() !== key) return label;
    } catch (error) { /* pas d'Intl.DisplayNames : on tombe sur l'autonyme */ }
    return LANG_AUTONYMS[key] || key.toUpperCase();
  }

  function renderVoice(root, audios, isFinal) {
    var block = root.querySelector("[data-bfl-review-voice]");
    if (!block) return;
    if (isFinal) writeAudioMemo(audios.length > 0);
    if (!audios.length) {
      if (!isFinal) return;
      block.hidden = true;
      block.classList.remove("is-pending");
      return;
    }

    if (root.bflReviewAudio) {
      try { root.bflReviewAudio.pause(); } catch (error) { /* noop */ }
    }

    var quoteEl = block.querySelector("[data-bfl-voice-quote]");
    var byEl = block.querySelector("[data-bfl-voice-by]");
    var ctaEl = block.querySelector("[data-bfl-voice-cta]");
    var timeEl = block.querySelector("[data-bfl-voice-time]");
    var iconEl = block.querySelector("[data-bfl-voice-icon]");
    var toggle = block.querySelector("[data-bfl-voice-toggle]");
    var navEl = block.querySelector("[data-bfl-voice-nav]");
    var seekEl = block.querySelector("[data-bfl-voice-seek]");
    var fillEl = block.querySelector("[data-bfl-voice-fill]");
    var langEl = block.querySelector("[data-bfl-voice-lang]");
    var pageLang = text(root.dataset.lang, "").toLowerCase();

    var listenLabel = text(root.dataset.voiceListenLabel, "Listen to her voice");
    var ofLabel = text(root.dataset.voiceOfLabel, "The voice of");
    var fallbackLabel = text(root.dataset.voiceFallbackLabel, "Client audio");
    var playLabel = text(root.dataset.audioPlayLabel, "Play");
    var pauseLabel = text(root.dataset.audioPauseLabel, "Pause");

    var current = 0;
    var audio = new Audio();
    // metadata : la durée doit être lisible AVANT de lancer — c'est elle qui
    // dit au visiteur ce qu'il engage.
    audio.preload = "metadata";
    root.bflReviewAudio = audio;

    function syncIcon() {
      iconEl.innerHTML = audio.paused ? PLAY_SVG : PAUSE_SVG;
      toggle.setAttribute("aria-label", audio.paused ? playLabel : pauseLabel);
    }

    function syncTime() {
      var value = audio.currentTime > 0 ? audio.currentTime : audio.duration;
      timeEl.textContent = Number.isFinite(value) && value > 0 ? formatTime(value) : "—";
    }

    function syncProgress() {
      var ratio = audio.duration > 0 ? audio.currentTime / audio.duration : 0;
      fillEl.style.width = Math.min(100, ratio * 100) + "%";
      syncTime();
    }

    function select(index, autoplay) {
      current = index;
      var entry = audios[index];
      var name = text(entry.authorName, "");
      var quote = text(entry.quote, "");
      var origin = text(entry.origin, "");
      var entryLang = text(entry.lang, "").toLowerCase();

      // Sans phrase saisie, on retombe sur « La voix de <prénom> » plutôt que
      // sur un bloc vide.
      quoteEl.textContent = quote ? "“" + quote + "”" : "";
      if (quote) {
        byEl.innerHTML = "";
        if (name) {
          byEl.appendChild(document.createTextNode("— "));
          byEl.appendChild(makeElement("span", "bfl-review-voice__name", name));
        }
      } else {
        byEl.innerHTML = "";
        byEl.appendChild(document.createTextNode(name ? ofLabel + " " : ""));
        byEl.appendChild(makeElement("span", "bfl-review-voice__name", name || fallbackLabel));
      }

      // La provenance suit le prénom — « — Salma, Dubaï ». C'est elle qui donne
      // envie d'écouter ; sans prénom saisi elle tient la ligne toute seule.
      if (origin) {
        if (byEl.childNodes.length) byEl.appendChild(document.createTextNode(", "));
        byEl.appendChild(makeElement("span", "bfl-review-voice__origin", origin));
      }

      // La citation est écrite dans la langue de la note, pas dans celle de la
      // page : sans lang/dir l'arabe et l'hébreu se rendent à l'envers.
      if (entryLang) {
        quoteEl.setAttribute("lang", entryLang);
        quoteEl.setAttribute("dir", RTL_LANGS[entryLang] ? "rtl" : "ltr");
      } else {
        quoteEl.removeAttribute("lang");
        quoteEl.removeAttribute("dir");
      }

      if (langEl) {
        var langLabel = entryLang && entryLang !== pageLang ? languageName(entryLang, pageLang) : "";
        langEl.textContent = langLabel;
        langEl.hidden = !langLabel;
      }

      ctaEl.textContent = listenLabel;
      audio.src = entry.audioUrl;
      fillEl.style.width = "0%";
      timeEl.textContent = "—";
      var counter = block.querySelector("[data-bfl-voice-count]");
      if (counter) counter.textContent = (index + 1) + "/" + audios.length;
      if (autoplay) {
        var promise = audio.play();
        if (promise && promise.catch) promise.catch(function () {});
      }
      syncIcon();
    }

    // Sélecteur si plusieurs notes vocales. ‹ et › sont des caractères Bidi
    // mirrorés : en RTL le navigateur les retourne seul, ne pas les échanger ici.
    if (audios.length > 1) {
      navEl.textContent = "";
      var prev = makeElement("button", "bfl-review-voice__arrow", "‹");
      var counterEl = makeElement("span", "bfl-review-voice__count", "1/" + audios.length);
      var next = makeElement("button", "bfl-review-voice__arrow", "›");
      prev.type = "button";
      next.type = "button";
      counterEl.setAttribute("data-bfl-voice-count", "");
      prev.addEventListener("click", function () { select((current - 1 + audios.length) % audios.length, true); });
      next.addEventListener("click", function () { select((current + 1) % audios.length, true); });
      navEl.appendChild(prev);
      navEl.appendChild(counterEl);
      navEl.appendChild(next);
      navEl.hidden = false;
    } else {
      navEl.hidden = true;
    }

    toggle.addEventListener("click", function () {
      if (audio.paused) {
        var promise = audio.play();
        if (promise && promise.catch) promise.catch(function () {});
      } else {
        audio.pause();
      }
    });

    audio.addEventListener("play", syncIcon);
    audio.addEventListener("pause", syncIcon);
    audio.addEventListener("timeupdate", syncProgress);
    audio.addEventListener("loadedmetadata", syncTime);
    audio.addEventListener("ended", function () {
      fillEl.style.width = "0%";
      audio.currentTime = 0;
      syncIcon();
      syncTime();
    });

    seekEl.addEventListener("click", function (event) {
      if (!audio.duration) return;
      var rect = seekEl.getBoundingClientRect();
      var offset = event.clientX - rect.left;
      var ratio = Math.min(1, Math.max(0, offset / rect.width));
      // En RTL la barre se lit de droite à gauche.
      if (String(root.getAttribute("dir") || "").toLowerCase() === "rtl") ratio = 1 - ratio;
      audio.currentTime = ratio * audio.duration;
      syncProgress();
    });

    select(0, false);
    block.hidden = false;
    block.classList.remove("is-pending");
  }

  /* ---------- Slider ---------- */
  function setActive(root, index) {
    var slides = Array.from(root.querySelectorAll(".bfl-google-reviews__slide"));
    var dots = Array.from(root.querySelectorAll(".bfl-google-reviews__dot"));
    slides.forEach(function (slide, slideIndex) {
      slide.classList.toggle("is-active", slideIndex === index);
    });
    dots.forEach(function (dot, dotIndex) {
      dot.classList.toggle("is-active", dotIndex === index);
    });
    root.dataset.activeIndex = String(index);
  }

  function hide(root) {
    root.hidden = true;
    root.classList.remove("is-ready");
    root.setAttribute("aria-hidden", "true");
  }

  function show(root) {
    root.hidden = false;
    root.classList.add("is-ready");
    root.setAttribute("aria-hidden", "false");
  }

  function startAutoplay(root, count) {
    if (count < 2) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    var interval = Math.max(3000, Number(root.dataset.autoplayInterval || 6000));
    var timer = 0;
    var paused = false;

    function tick() {
      if (paused) return;
      var current = Number(root.dataset.activeIndex || 0);
      setActive(root, (current + 1) % count);
    }

    root.addEventListener("mouseenter", function () { paused = true; });
    root.addEventListener("mouseleave", function () { paused = false; });
    root.addEventListener("focusin", function () { paused = true; });
    root.addEventListener("focusout", function () { paused = false; });
    window.clearInterval(timer);
    timer = window.setInterval(tick, interval);
  }

  function render(root, reviews, audios, config, isFinal) {
    var track = root.querySelector("[data-bfl-google-reviews-track]");
    var dots = root.querySelector("[data-bfl-google-reviews-dots]");
    if (!track || !dots || !reviews.length) {
      hide(root);
      return;
    }

    applyConfig(root, config);

    track.textContent = "";
    dots.textContent = "";
    reviews.forEach(function (review, index) {
      track.appendChild(makeSlide(root, review, index));
      var dot = makeElement("span", "bfl-google-reviews__dot");
      if (index === 0) dot.classList.add("is-active");
      dots.appendChild(dot);
    });
    setActive(root, 0);
    renderVoice(root, audios, isFinal);
    show(root);
    startAutoplay(root, reviews.length);
  }

  function init(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    var pageLang = text(root.dataset.lang, "");
    var fallback = readFallback(root);
    var hasRenderedFallback = fallback.reviews.length > 0;
    if (hasRenderedFallback) render(root, fallback.reviews, fallback.audios, null, false);
    // Visite précédente sans note vocale : on replie tout de suite la barre.
    if (readAudioMemo() === "0") renderVoice(root, [], true);

    var controller = window.AbortController ? new AbortController() : null;
    var timeout = window.setTimeout(function () {
      if (controller) controller.abort();
      if (hasRenderedFallback) renderVoice(root, [], true);
      else hide(root);
    }, 12000);

    window.fetch(root.dataset.endpoint || "/apps/appointments/google-reviews", {
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        if (!response.ok) throw new Error("google_reviews_unavailable");
        return response.json();
      })
      .then(function (payload) {
        window.clearTimeout(timeout);
        var reviews = reviewListFromPayload(payload, pageLang);
        var audios = audioListFromPayload(payload);
        if (reviews.length) {
          render(root, reviews, audios, payload && payload.widgetConfig, true);
        } else if (!hasRenderedFallback) {
          hide(root);
        } else {
          renderVoice(root, [], true);
        }
      })
      .catch(function () {
        window.clearTimeout(timeout);
        if (hasRenderedFallback) renderVoice(root, [], true);
        else hide(root);
      });
  }

  function initAll() {
    document.querySelectorAll("[data-bfl-google-reviews]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
